/**
 * The container details an invoicing service can compose its line description
 * from.
 *
 * Mirrors backend/config/invoiceDetailFields.js — same keys, same order. The
 * order matters: it is the order the composed description is built in, so the
 * list a user ticks reads the same way whatever order they tick it in. Keep
 * the two files in step; the backend validates against its own copy, so a key
 * added only here is rejected on save.
 *
 * `sample` is what the service form shows beside each checkbox. Examples, not
 * live data — the form is configuring a service, which has no container in
 * front of it; the point is to show the shape of the value you are opting into.
 */

export const INVOICE_DETAIL_KEYS = [
  "blNumber",
  "bookingRef",
  "orderNumber",
  "containerNumber",
  "referenceNumber",
  "loadingPlace",
  "loadingDate",
] as const;

export type InvoiceDetailKey = (typeof INVOICE_DETAIL_KEYS)[number];

export const INVOICE_DETAIL_SAMPLES: Record<InvoiceDetailKey, string> = {
  blNumber: "BL-123456",
  bookingRef: "BK-208841",
  orderNumber: "CO001",
  containerNumber: "MSDU7480977",
  referenceNumber: "PO-7788",
  loadingPlace: "REINUAUTO OÜ",
  loadingDate: "12.08.2026",
};

/**
 * A preview of the description a selection would produce.
 *
 * Built from the samples above and joined the way the backend joins real
 * values — a single space, in registry order — so the form can show what it is
 * configuring rather than describing it.
 */
export const previewDetailDescription = (fields: string[]): string =>
  INVOICE_DETAIL_KEYS.filter((key) => fields.includes(key))
    .map((key) => INVOICE_DETAIL_SAMPLES[key])
    .join(" ");
